import React from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Rocket,
  FileText,
  CheckCircle,
  ArrowRight,
  MapPin,
  Landmark,
  FileSignature,
  Banknote,
  Shield,
  Users,
  Briefcase,
  Globe,
  TrendingUp,
  Layers,
  HelpCircle,
  Phone,
  MessageSquare,
  Handshake,
  Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';
import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema, serviceSchema, faqSchema } from '../utils/seoSchemas';
import RelatedServices from '../components/RelatedServices';

const CompanyFormationUAE = () => {
  usePageSeo({
    title: 'Company Formation in UAE | Mainland & Free Zone Setup',
    description:
      'End-to-end UAE company formation — Mainland & Free Zone setup, trade licensing, bank account & post-setup compliance (VAT, CT, AML). Start smoothly with WPAA.',
    path: '/company-formation-uae'
  });

  const services = [
    { title: 'Business Setup in UAE Mainland', description: 'Full support for Mainland company registration with DED and relevant authorities.', icon: Landmark },
    { title: 'Free Zone Company Formation', description: 'Setup across DMCC, DIC, SAIF, Hamriyah and other major UAE Free Zones.', icon: Globe },
    { title: 'Trade License Registration & Renewal', description: 'End-to-end trade licence issuance and annual renewal management.', icon: FileText },
    { title: 'Business Activity Selection Guidance', description: 'Expert advice on choosing the right activity codes for your business plan.', icon: Target },
    { title: 'Corporate Structure Advisory', description: 'Strategic guidance on shareholding, holding structures and subsidiaries.', icon: Layers },
    { title: 'Shareholding & Legal Documentation', description: 'MOA, AOA, shareholder agreements and all legal paperwork prepared correctly.', icon: FileSignature },
    { title: 'Bank Account Assistance', description: 'Introductions and support for opening corporate bank accounts in the UAE.', icon: Banknote },
    { title: 'Post-Setup Compliance Support', description: 'Ongoing VAT, Corporate Tax and AML compliance from Day 1 of operations.', icon: Shield }
  ];

  const mainlandBenefits = [
    'Trade across UAE without restrictions',
    'Government project eligibility',
    'Flexible office location'
  ];

  const freezoneBenefits = [
    '100% foreign ownership',
    'Tax benefits (subject to conditions)',
    'Simplified setup process'
  ];

  const whoNeedsFormation = [
    { text: 'Entrepreneurs starting a new business', icon: Rocket },
    { text: 'Foreign investors entering UAE market', icon: Globe },
    { text: 'Freelancers & consultants', icon: Briefcase },
    { text: 'SMEs expanding operations', icon: TrendingUp },
    { text: 'Existing businesses restructuring', icon: Layers }
  ];

  const process = [
    { step: '01', title: 'Consultation & Requirement Analysis', description: 'Understand your business model, goals and jurisdiction preferences.' },
    { step: '02', title: 'Activity & Jurisdiction Selection', description: 'Advise on the best activity codes and Mainland vs Free Zone choice.' },
    { step: '03', title: 'Trade Name Reservation', description: 'Secure your trade name and obtain initial approval from authorities.' },
    { step: '04', title: 'Documentation & License Processing', description: 'Prepare and submit all legal and licensing documents.' },
    { step: '05', title: 'Company Registration & License', description: 'Receive your issued trade licence and company registration.' },
    { step: '06', title: 'Bank Account Opening Support', description: 'Facilitate corporate bank account opening with our banking partners.' },
    { step: '07', title: 'Post-Setup Compliance', description: 'VAT, Corporate Tax and AML registration so you are compliant from Day 1.' }
  ];

  const whyChooseUs = [
    'Expert guidance on UAE regulations',
    'Support across Mainland & Free Zones',
    'Fast and hassle-free process',
    'Transparent pricing',
    'End-to-end support (Audit | Tax | Advisory)'
  ];

  const postSetupItems = [
    { title: 'Accounting & Bookkeeping', icon: FileText },
    { title: 'VAT Registration & Filing', icon: Shield },
    { title: 'Corporate Tax Registration (9%)', icon: Landmark },
    { title: 'AML / goAML Compliance', icon: CheckCircle }
  ];

  const faqs = [
    { q: 'How long does company formation take in UAE?', a: 'Typically 3 to 10 working days depending on the jurisdiction and chosen activity. Free Zones are often faster than Mainland for straightforward setups.' },
    { q: 'Can a foreigner own 100% of a company in UAE?', a: 'Yes. 100% foreign ownership is permitted in all UAE Free Zones, and for a wide list of Mainland activities under the amended Commercial Companies Law.' },
    { q: 'Do I need an office to start a company?', a: 'It depends on the licence type. Many Free Zones offer flexi-desk or shared-office options that satisfy the physical presence requirement at a lower cost.' },
    { q: 'What is the cost of company formation in UAE?', a: 'Costs vary based on jurisdiction (Mainland or Free Zone), licence type, number of visas, office type and activity. We provide transparent, fixed-price quotations after a short consultation.' },
    { q: 'Will I need VAT or Corporate Tax registration?', a: 'It depends on your business activity and expected revenue. VAT is mandatory above AED 375,000 turnover; Corporate Tax registration is mandatory for most UAE entities. We guide you through both.' }
  ];

  return (
    <div className="min-h-screen">
      <JsonLd
        id="ld-cf-service"
        data={serviceSchema({
          name: 'Company Formation Services in UAE',
          description:
            'End-to-end UAE company formation across Mainland and Free Zones, trade license registration, bank account assistance and post-setup compliance.',
          path: '/company-formation-uae',
          serviceType: 'Company Formation Services'
        })}
      />
      <JsonLd
        id="ld-cf-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: 'Company Formation UAE', path: '/company-formation-uae' }
        ])}
      />
      <JsonLd id="ld-cf-faq" data={faqSchema(faqs)} />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-purple-900 via-purple-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-300 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-6">
              <Building2 className="text-purple-400" size={32} />
              <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">Company Formation</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Company Formation Services in UAE
            </h1>
            <p className="text-2xl text-purple-200 mb-6 font-semibold">
              Start Your Business in UAE with Confidence
            </p>
            <p className="text-xl text-gray-300 mb-6 leading-relaxed max-w-3xl">
              Setting up a business in the UAE can be complex — choosing the right structure, jurisdiction, and licensing authority is critical.
            </p>
            <p className="text-lg text-gray-400 mb-8 max-w-3xl">
              At White Paper Accounts Auditing (WPAA), we provide end-to-end company formation services to help you start your business smoothly, compliantly, and efficiently.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact?service=company-formation">
                <Button size="lg" className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-6 text-lg" data-testid="cf-consultation-btn">
                  Book Free Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="tel:+971501219341" data-testid="cf-call-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">Our Expertise</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Our Company Formation Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We support businesses across Mainland and Free Zones — from incorporation to ongoing compliance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-2 hover:border-purple-500 transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="bg-purple-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="text-purple-600" size={28} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-sm text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mainland vs Free Zone */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">Choose the Right Structure</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Mainland vs Free Zone — What's Right for You?</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Mainland */}
            <Card className="border-2 border-purple-200 hover:border-purple-500 transition-all">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-purple-600 w-14 h-14 rounded-xl flex items-center justify-center">
                    <Landmark className="text-white" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Mainland Company</h3>
                </div>
                <ul className="space-y-3">
                  {mainlandBenefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="text-purple-600 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700">{b}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Free Zone */}
            <Card className="border-2 border-purple-200 hover:border-purple-500 transition-all">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-purple-600 w-14 h-14 rounded-xl flex items-center justify-center">
                    <Globe className="text-white" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Free Zone Company</h3>
                </div>
                <ul className="space-y-3">
                  {freezoneBenefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="text-purple-600 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700">{b}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="max-w-3xl mx-auto mt-10 bg-purple-50 border border-purple-200 rounded-xl p-6 flex items-start gap-4">
            <Handshake className="text-purple-600 flex-shrink-0 mt-1" size={28} />
            <p className="text-lg text-purple-900 font-medium">
              We help you choose the right structure based on your business goals, activity and long-term plans.
            </p>
          </div>
        </div>
      </section>

      {/* Who Needs */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">Target Clients</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Who Needs Company Formation Services?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {whoNeedsFormation.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="text-purple-600" size={24} />
                    </div>
                    <p className="text-gray-700 font-medium pt-2">{item.text}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">How We Work</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Our Process — Simple & Transparent</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {process.map((item, index) => (
              <div key={index} className="relative bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="absolute -top-4 -left-4 bg-purple-600 text-white w-14 h-14 rounded-xl flex items-center justify-center font-bold text-lg shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mt-4 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose WPAA */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">Why WPAA</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-8">Why Choose WPAA?</h2>
              <div className="space-y-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="text-purple-600 flex-shrink-0" size={24} />
                    <p className="text-lg text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-8 text-white">
              <Users className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Trusted Business Setup Partner</h3>
              <p className="text-purple-100 mb-6">
                Our team has helped entrepreneurs, investors and SMEs successfully establish their businesses across UAE Mainland and all major Free Zones.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-3xl font-bold">18+</p>
                  <p className="text-sm text-purple-200">Years Experience</p>
                </div>
                <div>
                  <p className="text-3xl font-bold">200+</p>
                  <p className="text-sm text-purple-200">Clients Served</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Post-Setup Compliance */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-50 to-white border-y-4 border-purple-400">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">Important</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-4">Post-Setup Compliance Support</h2>
              <p className="text-lg text-gray-700">
                Starting a company is just the first step. We also support you with:
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              {postSetupItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="border-2 border-purple-200 bg-white text-center">
                    <CardContent className="p-6">
                      <div className="bg-purple-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4 mx-auto">
                        <Icon className="text-purple-600" size={28} />
                      </div>
                      <h3 className="text-base font-bold text-gray-900">{item.title}</h3>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="bg-purple-600 rounded-xl p-6 text-white text-center">
              <p className="text-lg font-medium">
                So your business stays fully compliant from Day 1.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">FAQs</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white border border-gray-200 rounded-xl px-6 shadow-sm"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-purple-600 hover:no-underline">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="text-purple-600 flex-shrink-0 mt-1" size={20} />
                      <span>{faq.q}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-base leading-relaxed pl-8">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <RelatedServices
        services={[
          { title: 'Accounting Services', description: 'Day-one bookkeeping and financial setup for your new UAE entity.', path: '/accounting-services-uae', accent: 'indigo' },
          { title: 'VAT Services', description: 'VAT registration and filing for your new Mainland or Free Zone company.', path: '/vat-services-uae', accent: 'blue' },
          { title: 'Corporate Tax Services', description: 'Stay compliant with UAE Corporate Tax from Day 1 of operations.', path: '/corporate-tax-uae', accent: 'teal' }
        ]}
      />

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-4xl font-bold mb-6">Start Your Business Today</h2>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
            Let's make your business setup simple and stress-free.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <Link to="/contact?service=company-formation">
              <Button size="lg" className="bg-white text-purple-700 hover:bg-gray-100 px-8 py-6 text-lg" data-testid="cf-cta-consultation-btn">
                Book Your FREE Consultation
              </Button>
            </Link>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="cf-whatsapp-btn">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                WhatsApp Us
              </Button>
            </a>
          </div>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-purple-100">
            <a href="tel:+971509987431" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="cf-phone-1">
              <Phone size={18} />
              <span>+971 50 998 7431</span>
            </a>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="cf-phone-2">
              <MessageSquare size={18} />
              <span>WhatsApp +971 50 121 9341</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompanyFormationUAE;
